/**
 * GapReportView — Liquidity / Rate gap report page.
 *
 * Output is always the gap matrix (segments × maturity buckets) for a
 * chosen snapshot date and report configuration (spec §6.1.3).
 *
 * The page owns only the gap-type toggle (Liquidity | Rate).
 * All other criteria (report config, snapshot date, dimension, segmentation,
 * currency) are managed by the embedded GapMatrixView.
 *
 * Export: CSV download of the displayed matrix is available inside GapMatrixView.
 * Full cashflow datamart export is planned for a future hop.
 */
import { useState } from 'react';
import { GapMatrixView } from './GapMatrixView';
import { cn } from '@/lib/utils';
import type { GapType } from './types';

// ── Prop interface ────────────────────────────────────────────────────────────

interface GapReportViewProps {
  gapType: GapType;
  title: string;
  description: string;
  accent?: 'liquidity' | 'rate';
}

// ── Main component ────────────────────────────────────────────────────────────

export function GapReportView({
  gapType: initialGapType,
  title,
  description,
  accent = 'liquidity',
}: GapReportViewProps) {

  const [formGapType, setFormGapType] = useState<GapType>(initialGapType);

  const accentClass =
    accent === 'rate'
      ? 'border-l-4 border-amber-500/60 pl-3'
      : 'border-l-4 border-primary/60 pl-3';

  return (
    <div className="space-y-5">

      {/* Page header */}
      <header className={accentClass}>
        <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
        <p className="text-sm text-muted-foreground">{description}</p>
      </header>

      {/* Gap type strip */}
      <div className="flex items-center gap-3">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Gap type
        </span>
        <div className="inline-flex items-center gap-1 rounded-md border bg-muted/40 p-0.5">
          {(
            [
              { value: 'LIQUIDITY',    label: 'Liquidity' },
              { value: 'INTEREST_RATE', label: 'Rate'     },
            ] as const
          ).map(({ value, label }) => {
            const active = formGapType === value;
            return (
              <button
                key={value}
                type="button"
                onClick={() => setFormGapType(value)}
                className={cn(
                  'rounded px-3 py-1 text-xs font-medium transition-colors',
                  active
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Gap matrix — owns report selector, snapshot date, run, result table, export */}
      <GapMatrixView gapType={formGapType} />

    </div>
  );
}
